// src/services/credit-service.js
// FIXED VERSION — consistent mapping, explicit userId handling, transaction-safe consumption

import { logger } from "../utils/logger";

// --- helpers -----------------------------------------------------------------

// Map any fanType to the backend credit bucket
const mapFanTypeToCredit = (fanType) => {
  const ft = String(fanType || "").toLowerCase();
  if (ft === "casual") return null;               // no credits required
  if (ft === "dedicated") return "dedicated";     // dedicated bucket
  if (ft === "diehard" || ft === "expert") return "expert"; // unified expert bucket
  logger.warn?.("Unknown fanType; defaulting to 'expert':", fanType);
  return "expert";
};

const DEFAULT_CREDITS = { dedicated: 0, expert: 0 };

// --- service -----------------------------------------------------------------

class CreditService {
  constructor() {
    this.initialized = false;
    this.activeConsumptions = new Set(); // prevent duplicate consumes per (user, game, type)
  }

  async initialize() {
    if (this.initialized) return;
    if (!window?.firebase || !window?.db) {
      throw new Error("Firebase not initialized");
    }
    this.initialized = true;
    logger.success?.("✅ Credit Service initialized");
  }

  get FieldValue() {
    // Convenience accessor
    return window.firebase.firestore.FieldValue;
  }

  get firestore() {
    return window.firebase.firestore();
  }

  // Safely get the current Firebase auth userId
  getCurrentUserId() {
    try {
      const user = window.firebase?.auth?.()?.currentUser;
      return user?.uid || null;
    } catch (err) {
      logger.error?.("Error getting current user:", err);
      return null;
    }
  }

  // --- balance ---------------------------------------------------------------

  async checkBalance(userId = null) {
    try {
      await this.initialize();
      const uid = userId || this.getCurrentUserId();
      if (!uid) {
        logger.debug?.("No user; returning zero credits");
        return { ...DEFAULT_CREDITS };
      }
      const snap = await window.db.collection("users").doc(uid).get();
      if (!snap.exists) return { ...DEFAULT_CREDITS };
      const data = snap.data() || {};
      const credits = data.credits && typeof data.credits === "object" ? data.credits : DEFAULT_CREDITS;
      return {
        dedicated: Number(credits.dedicated || 0),
        expert: Number(credits.expert || 0),
      };
    } catch (err) {
      logger.error?.("checkBalance failed:", err);
      return { ...DEFAULT_CREDITS };
    }
  }

  // Optional realtime listener
  listenBalance(userId, cb) {
    if (!window?.db || !userId) return () => {};
    try {
      const ref = window.db.collection("users").doc(userId);
      const unsub = ref.onSnapshot(
        (doc) => {
          const data = doc.exists ? doc.data() : {};
          const credits = data?.credits || DEFAULT_CREDITS;
          cb({
            dedicated: Number(credits.dedicated || 0),
            expert: Number(credits.expert || 0),
          });
        },
        (err) => logger.error?.("listenBalance error:", err)
      );
      return unsub;
    } catch (err) {
      logger.error?.("listenBalance setup failed:", err);
      return () => {};
    }
  }

  // --- consume ---------------------------------------------------------------

  /**
   * Consume exactly one credit for a given fanType and gameId.
   * Uses a Firestore transaction to guarantee atomicity & prevent negatives.
   */
  async consumeCredit(fanType, gameId, userId = null) {
    await this.initialize();

    const uid = userId || this.getCurrentUserId();
    if (!uid) throw new Error("User ID required - no authenticated user found");
    if (!fanType || !gameId) throw new Error("Missing required parameters: fanType and gameId are required");

    const creditType = mapFanTypeToCredit(fanType);
    if (!creditType) {
      // casual → no credit required
      return { success: true, creditType: null, creditsUsed: 0, previousBalance: null, newBalance: null };
    }

    const key = `${uid}::${gameId}::${creditType}`;
    if (this.activeConsumptions.has(key)) {
      throw new Error("Credit consumption already in progress for this game");
    }
    this.activeConsumptions.add(key);

    try {
      const userRef = window.db.collection("users").doc(uid);

      const result = await this.firestore.runTransaction(async (tx) => {
        const userSnap = await tx.get(userRef);
        if (!userSnap.exists) throw new Error(`User document not found for ID: ${uid}`);

        const userData = userSnap.data() || {};
        const credits = userData.credits && typeof userData.credits === "object" ? { ...userData.credits } : { ...DEFAULT_CREDITS };
        const current = Number(credits[creditType] || 0);

        if (current <= 0) {
          throw new Error(`No ${fanType} credits available. Current balance: ${current}`);
        }

        // Decrement exactly one
        const newCredits = { ...credits, [creditType]: current - 1 };

        // Track usage under creditUsage[gameId]
        const usagePath = `creditUsage.${gameId}`;
        const updateData = {
          credits: newCredits,
          updatedAt: this.FieldValue.serverTimestamp(),
          [usagePath]: {
            fanType,
            creditType,
            creditsUsed: 1,
            usedAt: this.FieldValue.serverTimestamp(),
            gameId,
            userId: uid,
          },
        };

        tx.update(userRef, updateData);

        return {
          previousBalance: credits,
          newBalance: newCredits,
        };
      });

      logger.success?.("✅ Credit consumed", {
        userId: uid,
        fanType,
        creditType,
        gameId,
        previous: result.previousBalance[creditType],
        next: result.newBalance[creditType],
      });

      return {
        success: true,
        fanType,
        creditType,
        gameId,
        creditsUsed: 1,
        previousBalance: result.previousBalance,
        newBalance: result.newBalance,
        creditsRemaining: result.newBalance, // kept for callers that expect this field
      };
    } catch (err) {
      logger.error?.("consumeCredit failed:", err);
      throw err;
    } finally {
      this.activeConsumptions.delete(key);
    }
  }

  // --- add / grant -----------------------------------------------------------

  async addCredits(userId = null, fanType, amount, purchaseInfo = {}) {
    await this.initialize();
    const uid = userId || this.getCurrentUserId();
    if (!uid) throw new Error("User ID required for adding credits");

    const creditType = mapFanTypeToCredit(fanType);
    if (!creditType) return { success: true, creditsAdded: 0, newBalance: null }; // casual path no-op

    const userRef = window.db.collection("users").doc(uid);

    const { newBalance } = await this.firestore.runTransaction(async (tx) => {
      const snap = await tx.get(userRef);
      const base = snap.exists ? (snap.data() || {}) : {};
      const current = base.credits && typeof base.credits === "object" ? { ...base.credits } : { ...DEFAULT_CREDITS };

      const added = Number(amount || 0);
      if (!Number.isFinite(added) || added <= 0) throw new Error("Amount must be a positive number");

      const next = { ...current, [creditType]: Number(current[creditType] || 0) + added };

      const purchaseKey = `purchases.${Date.now()}`;
      tx.set(
        userRef,
        {
          ...base,
          credits: next,
          [purchaseKey]: {
            fanType,
            creditType,
            creditsAdded: added,
            purchasedAt: this.FieldValue.serverTimestamp(),
            userId: uid,
            ...purchaseInfo,
          },
          updatedAt: this.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );

      return { newBalance: next };
    });

    logger.success?.("✅ Credits added", { userId: uid, fanType, creditType, amount, newBalance });
    return { success: true, creditsAdded: Number(amount), newBalance };
  }

  // --- capability ------------------------------------------------------------

  async canUsePremiumAnalysis(userId = null, fanType) {
    try {
      if (String(fanType || "").toLowerCase() === "casual") return true;
      const credits = await this.checkBalance(userId);
      const ct = mapFanTypeToCredit(fanType);
      return Number(credits[ct] || 0) > 0;
    } catch (err) {
      logger.error?.("canUsePremiumAnalysis failed:", err);
      return false;
    }
  }

  // --- history / misc --------------------------------------------------------

  async getCreditUsageHistory(userId = null, limit = 10) {
    await this.initialize();
    const uid = userId || this.getCurrentUserId();
    if (!uid) throw new Error("User ID required for usage history");

    const snap = await window.db.collection("users").doc(uid).get();
    if (!snap.exists) return [];
    const data = snap.data() || {};
    const usage = data.creditUsage || {};

    const arr = Object.entries(usage).map(([gameId, record]) => ({ gameId, ...(record || {}) }));
    // Sort by usedAt (serverTimestamp), fallback to 0
    arr.sort((a, b) => {
      const at = a.usedAt?.seconds || a.usedAt?._seconds || 0;
      const bt = b.usedAt?.seconds || b.usedAt?._seconds || 0;
      return bt - at;
    });

    return arr.slice(0, Math.max(0, Number(limit || 10)));
  }

  async validateGroupMemberAccess(fanType, groupId, userId) {
    try {
      await this.initialize();
      const uid = userId || this.getCurrentUserId();
      if (!uid) throw new Error("User ID required");

      const credits = await this.checkBalance(uid);
      const ct = mapFanTypeToCredit(fanType);
      const hasCredits = Number(credits[ct] || 0) > 0;

      // Optional: check if group doc marks the user as already consumed/paid
      let userAlreadyPaid = false;
      try {
        const groupSnap = await window.db.collection("games").doc(groupId).get();
        if (groupSnap.exists) {
          const g = groupSnap.data() || {};
          userAlreadyPaid = Boolean(g?.groupMembers?.[uid]?.creditConsumed);
        }
      } catch (err) {
        logger.warn?.("validateGroupMemberAccess: group check failed (non-fatal):", err);
      }

      return {
        hasCredits,
        creditsAvailable: Number(credits[ct] || 0),
        userAlreadyPaid,
        canParticipate: hasCredits || userAlreadyPaid,
      };
    } catch (err) {
      logger.error?.("validateGroupMemberAccess failed:", err);
      return {
        hasCredits: false,
        creditsAvailable: 0,
        userAlreadyPaid: false,
        canParticipate: false,
        error: err.message,
      };
    }
  }

  // --- debug -----------------------------------------------------------------

  async debugCreditBalance(userId = null) {
    const uid = userId || this.getCurrentUserId();
    if (!uid) {
      logger.debug?.("debugCreditBalance: no user");
      return;
    }
    const snap = await window.db.collection("users").doc(uid).get();
    const data = snap.exists ? snap.data() : {};
    const credits = data?.credits || {};
    logger.credits?.("Current credits", {
      dedicated: Number(credits.dedicated || 0),
      expert: Number(credits.expert || 0),
    });
    return credits;
  }

  async testCreditConsumption(fanType = "dedicated") {
    try {
      const uid = this.getCurrentUserId();
      if (!uid) throw new Error("No authenticated user for test");

      const before = await this.checkBalance(uid);
      const gameId = `test_${Date.now()}`;
      const result = await this.consumeCredit(fanType, gameId, uid);
      const after = await this.checkBalance(uid);

      logger.success?.("Test consumption", { before, result, after });
      return { success: true, beforeBalance: before, result, afterBalance: after };
    } catch (err) {
      logger.error?.("testCreditConsumption failed:", err);
      return { success: false, error: err.message };
    }
  }
}

// Singleton
const creditService = new CreditService();
export default creditService;

// Expose for debugging (optional)
if (typeof window !== "undefined") {
  window.creditService = creditService;
  window.debugCredits = () => creditService.debugCreditBalance();
  window.testCreditConsumption = (fanType) => creditService.testCreditConsumption(fanType);
  window.testDedicatedCredit = () => creditService.testCreditConsumption("dedicated");
  window.testExpertCredit = () => creditService.testCreditConsumption("expert");
  logger.debug?.("🧪 CreditService debug helpers attached to window");
}
